import { connect } from 'react-redux';
import { get, groupBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import utilsXLS, { EXPORT_DECIMALS_COUNT } from '@commons/utils/makeXLS';

import { exitModal, exportReady, exportFailure } from '@commons/utils/exportModalWithProgress';
import ExportModalContent from '@lib/inpulse/ExportModal';

import { getClientInfo } from '@selectors/client';
import { getHasRecipeVariants } from '@selectors/featureProps';

import { product as productService } from '@services/product';
import recipeService from '@services/recipe';

import { buildProductRecipeRow, prepareProductForExport } from '../../utils/productExportRows';

const BATCH_PRODUCTS_TO_FETCH_AT_ONCE = 50;

export const getColumnsSettings = (hasVariantColumn) => [
  {
    propertyKey: 'product.id',
    name: i18next.t('GENERAL.ID'),
  },
  {
    propertyKey: 'product.name',
    name: i18next.t('GENERAL.NAME'),
  },
  {
    propertyKey: 'product.status',
    name: i18next.t('GENERAL.STATUS'),
  },
  {
    propertyKey: 'product.category',
    name: i18next.t('GENERAL.CATEGORY'),
  },
  {
    propertyKey: 'product.subCategory',
    name: i18next.t('GENERAL.SUB_CATEGORY'),
  },
  {
    propertyKey: 'product.composition',
    name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_PRODUCT_COMPOSITION'),
  },
  {
    propertyKey: 'recipe.channel',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_RECIPE_CHANNEL'),
  },
  ...(hasVariantColumn
    ? [
        {
          propertyKey: 'recipe.variantName',
          name: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_VARIANT_COLUMN'),
        },
      ]
    : []),
  {
    type: 'currency',
    propertyKey: 'recipe.recipeCost',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_RECIPE_COST'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    type: 'currency',

    propertyKey: 'product.priceHT',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_HT'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    type: 'percentage',
    propertyKey: 'recipe.ratioCost',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_RECIPE_RATIO_COST'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    propertyKey: 'product.vatRate',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_VAT_RATE'),
  },
  {
    type: 'currency',
    propertyKey: 'product.priceWithTaxes',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_INCLUDING_TAXES'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    type: 'percentage',
    propertyKey: 'recipe.marginRatio',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_MARGIN'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    propertyKey: 'product.sku',
    name: i18next.t('GENERAL.SKU'),
  },
  {
    propertyKey: 'product.shelfLife',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_SHELF_LIFE'),
  },
  {
    propertyKey: 'product.portionSizeInGrams',
    name: i18next.t('ADMIN.PRODUCTS.PORTION_SIZE_IN_GRAMS'),
  },
];

/**
 * Format a given data related to a product and make sure to get all information
 * that would be necessary for generating the export file
 *
 * @param {Object} data - The data to format
 * @param {Object} data.product - The product being treated
 * @param {Object[]} data.recipes - The list of recipes associated to the product
 * @param {Object} defaultChannelId - The default channel id of the client
 *
 * @return {Object[]} The list of formatted data from the given product and its associated recipes
 */
export const formatDataForXLSFile = (
  { product, recipes },
  channels,
  defaultChannelId,
  clientBrands,
  variantsByRecipeId = {},
  hasVariantColumn = false,
) => {
  prepareProductForExport(product, clientBrands, defaultChannelId);

  const mainVariantName = i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB');

  if (!recipes.length) {
    return [
      {
        product,
        recipe: {
          channel: i18next.t('ADMIN.RECIPES.EXPORT_VALUE_NO_ENTITY'),
          ...(hasVariantColumn ? { variantName: mainVariantName } : {}),
        },
      },
    ];
  }

  return recipes.flatMap((recipe) => {
    const isIngredient = recipe.isIngredient;
    const channel = channels.find(({ id }) => id === recipe.channelId) || {};
    const channelId = channel.id || defaultChannelId;

    const mainRawCost = isIngredient ? recipe.cost : recipe.cost[channelId];
    const mainRow = buildProductRecipeRow(
      product,
      recipe,
      channel,
      channelId,
      mainRawCost,
      hasVariantColumn ? { variantName: mainVariantName } : {},
    );

    // Ingredients have no variants; without the variant column, keep the single row per recipe.
    if (!hasVariantColumn || isIngredient) {
      return [mainRow];
    }

    const variantRows = (variantsByRecipeId[recipe.id] || []).map((variant) =>
      buildProductRecipeRow(
        product,
        recipe,
        channel,
        channelId,
        variant.costByChannelId?.[channelId],
        {
          variantName: variant.name,
        },
      ),
    );

    return [mainRow, ...variantRows];
  });
};

/**
 * Update the progress value from the elements that are still left to treat
 *
 * @param {String[]} productIds           - The list of product ids that are still to be treated
 * @param {Object[]} detailedProductList  - The list of product that has already been treated
 * @param {Function} setProgress          - Method to set the local state of the progress value
 *
 * @return {void}
 */
export function updateProgress(productIds, detailedProductList, setProgress) {
  const productLeft = productIds.length;
  const totalProducts = productLeft + detailedProductList.length;
  const updatedProgress = 100 - (productLeft / totalProducts) * 100;

  setProgress(updatedProgress);
}

/**
 * Generate the products XLS from the accumulated raw export data, expanding each recipe into its
 * main row plus one row per variant when the variant column is enabled.
 *
 * @returns {void}
 */
const generateProductsExport = ({
  rawProductDataList,
  clientChannels,
  defaultChannelId,
  clientBrands,
  hasMultipleBrands,
  hasSimpleModifierManagement,
  currency,
  variantsByRecipeId,
  hasVariantColumn,
}) => {
  const columnsSettings = getColumnsSettings(hasVariantColumn);

  if (hasMultipleBrands) {
    columnsSettings.splice(2, 0, {
      propertyKey: 'product.brandName',
      name: i18next.t('GENERAL.BRAND'),
    });
  }

  if (hasSimpleModifierManagement) {
    columnsSettings.splice(2, 0, {
      propertyKey: 'product.type',
      name: i18next.t('ADMIN.PRODUCTS.PRODUCT_TYPE_EXPORT_COLUMN_TITLE'),
    });
  }

  const rows = rawProductDataList.flatMap((data) =>
    formatDataForXLSFile(
      data,
      clientChannels,
      defaultChannelId,
      clientBrands,
      variantsByRecipeId,
      hasVariantColumn,
    ),
  );

  for (const row of rows) {
    if (get(row, 'product.cashierProductSku', null)) {
      columnsSettings.push({
        propertyKey: 'product.cashierProductSku',
        name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_SYNC_CASHIER_PRODUCT_SKU'),
      });

      break;
    }
  }

  const sheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.PRODUCT_PLURAL'),
    columnsSettings,
    rows,
    currency,
  );

  utilsXLS.makeXLS(i18next.t('GENERAL.PRODUCT_PLURAL'), [sheet]);
};

/**
 * Handle the progressive loading of products from the list of productIds that are still to fetch.
 * Accumulates the raw export data; variant resolution and XLS generation happen at the finalize step.
 *
 * @returns {void}
 */
export async function loadProductsByBatchOfIds(
  productIds,
  rawProductDataList,
  setProgress,
  setProductIds,
  setRawProductDataList,
  setFetchingPhase,
  setLoading,
  setTitle,
) {
  if (!productIds.length && !rawProductDataList.length) {
    return;
  }

  // If no more ids, all data has been fetched: resolve variants and generate at the finalize step
  if (!productIds.length) {
    setProgress(100);
    setFetchingPhase('finalizing');

    return;
  }

  const selectedProductIds = productIds.slice(0, BATCH_PRODUCTS_TO_FETCH_AT_ONCE);
  const remainingProductIds = productIds.slice(BATCH_PRODUCTS_TO_FETCH_AT_ONCE);

  try {
    const productsExportData = await productService.exportProducts(selectedProductIds);

    const updatedRawData = [...rawProductDataList, ...productsExportData];

    setRawProductDataList(updatedRawData);
    updateProgress(remainingProductIds, updatedRawData, setProgress);
    setProductIds(remainingProductIds);
  } catch {
    exportFailure(setLoading, setTitle);
  }
}

export const ProductExportModal = (props) => {
  const {
    params: { title, products, customParams, selectedStatus },
    client: { hasMultipleBrands, defaultChannelId, hasSimpleModifierManagement, clientChannels },
    currency,
    clientBrands,
    hasVariants,
    closeModal,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(title || '');
  const [productIdsToProcess, setProductIds] = useState([]);
  const [rawProductDataList, setRawProductDataList] = useState([]);

  // States for fetching all products by filters
  const [currentPage, setCurrentPage] = useState(1);
  const [allProductIds, setAllProductIds] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial'); // 'initial', 'fetching_ids', 'processing_details', 'finalizing'

  const fetchNextBatch = async () => {
    try {
      const { data, count } = await productService.getPaginatedProductsOfClient(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_PRODUCTS_TO_FETCH_AT_ONCE,
          search: '', // Don't apply search for export
        },
        {
          brandId: customParams.brandId,
          active: { eq: get(selectedStatus, 'filterValue', false) },
          categoryId: customParams.categoryId,
          isIngredient: customParams.isIngredient,
          subCategoryId: customParams.subCategoryId,
          priceWithTaxes: customParams.priceWithTaxes,
          nbCashierProductMappingCount: customParams.nbCashierProductMappingCount,
        },
      );

      const productIds = (data || []).map(({ id }) => id);

      // Update allProductIds with new batch
      const updatedAllProductIds = [...allProductIds, ...productIds];
      setAllProductIds(updatedAllProductIds);

      // Check if we have more data to fetch
      if (
        data &&
        data.length === BATCH_PRODUCTS_TO_FETCH_AT_ONCE &&
        updatedAllProductIds.length < count
      ) {
        // Continue with next batch
        setCurrentPage(currentPage + 1);
      } else {
        // All product IDs fetched, now start the detailed export process
        setFetchingPhase('processing_details');
        setProductIds([...updatedAllProductIds]);
      }
    } catch {
      exportFailure(setLoading, setTitle);
    }
  };

  // Initialize: either use provided products or fetch all by filters
  useEffect(() => {
    if (products && products.length) {
      // Export selected products directly
      const productIds = products.map(({ id }) => id);
      setProductIds(productIds);
      setFetchingPhase('processing_details');
    } else if (customParams && selectedStatus) {
      // Start fetching all products by filters
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Fetch products batch by batch when in fetching_ids phase
  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Fetch the product export data in batches (accumulates raw data)
  useEffect(() => {
    if (fetchingPhase !== 'processing_details') {
      return;
    }

    loadProductsByBatchOfIds(
      [...productIdsToProcess],
      rawProductDataList,
      setProgress,
      setProductIds,
      setRawProductDataList,
      setFetchingPhase,
      setLoading,
      setTitle,
    );
  }, [productIdsToProcess, fetchingPhase]);

  // Resolve the variant costs of the exported recipes, then generate the XLS
  useEffect(() => {
    if (fetchingPhase !== 'finalizing') {
      return;
    }

    const finalize = async () => {
      try {
        const recipeIds = [
          ...new Set(
            rawProductDataList.flatMap(({ recipes }) =>
              (recipes || []).filter(({ isIngredient }) => !isIngredient).map(({ id }) => id),
            ),
          ),
        ];

        const compositions =
          hasVariants && recipeIds.length
            ? (await recipeService.getVariantCompositions(recipeIds)).compositions
            : [];

        generateProductsExport({
          rawProductDataList,
          clientChannels,
          defaultChannelId,
          clientBrands,
          hasMultipleBrands,
          hasSimpleModifierManagement,
          currency,
          variantsByRecipeId: groupBy(compositions, 'recipeId'),
          hasVariantColumn: compositions.length > 0,
        });

        exportReady(setLoading, setTitle);
      } catch {
        exportFailure(setLoading, setTitle);
      }
    };

    finalize();
  }, [fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  clientBrands: state.baseReducer.brands,
  currency: state.baseReducer.currency,
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
});

export default connect(mapStateToProps, null)(ProductExportModal);
